/* eslint-disable */
import React, { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { passwordIcon, passwordType } from "components/password-and-confirm-password/passwordcheckerversion2/PasswordCheckerVersion2";
import { Button } from "components/ui/button/Button";
import Form from "components/ui/form/Form";
import { FormInput } from "components/ui/form/FormInput";
import { LoginData, usePostLoginMutation } from "./loginService";
import * as Constant from "utilities/Constant";
import { emailAddress, password_input as password } from "utilities/formSchemas";
import { LoadingOverlay } from "components/ui/loading-spinners";
import "./login.scss";
import { IMetricTelemetry } from "@microsoft/applicationinsights-web";
import { setCredentials, setIsLogin, setUserId } from "features/authSlice";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ErrorType } from "utilities/error";
import { ValidateOtpResponse } from "pages/authentication/authenticationService";
import { setFSUserProperties } from "utilities/full-story";
import { getLastName } from "utilities/string";
import useDesktopChecker from "hooks/useDesktopChecker";
import { DecryptedUser, LeadProvider } from "pages/registration/registrationService";
import homeGraphic from "assets/images/HomeGraphic.png";
import { Consent } from "components/consent/Consent";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { AdaFooter } from "components/ada-footer/Ada-footer";

const formSchema = z.object({
  email: emailAddress,
  password,
});

export default function Login() {
  const location = useLocation();
  const dispatch = useDispatch();
  const pathsToRefresh = ["/application", "/dashboard"];
  if (
    location.state != null &&
    typeof location.state === "object" &&
    "previousPath" in location.state &&
    pathsToRefresh.some((str) => location.state.previousPath.includes(str)) // eslint-disable-line
  ) {
    history.replaceState({}, "", "/");
    window.location.reload();
  }
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm({
    resolver: zodResolver(formSchema),
  });
  const [error, setError] = useState(0);
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const [sendRequest, { isSuccess, data, isLoading: isLoginLoading, isError: isLoginError }] = usePostLoginMutation();
  const onSubmit = (data: LoginData) =>
    sendRequest(data)
      .unwrap()
      .catch((error: ErrorType) => {
        setError(error.status);
      });
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const navigate = useNavigate();
  const isDesktop = useDesktopChecker();

  if (isSuccess && data) {
    if ("status" in data && data.status.toLowerCase() === "completed") {
      const response = data as ValidateOtpResponse;
      const lastName = getLastName(response.name);
      setFSUserProperties(getValues("email") as string, lastName.toLowerCase());
      dispatch(setCredentials(response));
      dispatch(setIsLogin(true));
      navigate("/my-loans", { replace: true });
    } else if ("status" in data && data.status === "MUST_CHANGE_PASSWORD") {
      dispatch(setUserId(data.id));
      navigate("/updatepassword", { replace: true });
    } else {
      setFSUserProperties(getValues("email") as string);
      navigate("/mfa", { replace: true });
    }
  }
  if (isLoginLoading) return <LoadingOverlay text="Logging in..." />;

  const newRelicEnabled = Constant.NEW_RELIC_ENABLED;
  if (!newRelicEnabled) {
    const metricData: IMetricTelemetry = {
      average: 100,
      name: "React Component Engaged Time (seconds)",
      sampleCount: 1,
    };
    const appInsights = useAppInsightsContext();
    const additionalProperties = { "Component Name": "Login" };
    appInsights.trackMetric(metricData, additionalProperties);
  }

  const formatContent = (content: string[] | undefined) => {
    return content?.map((line) => (
      <React.Fragment key={line}>
        {line} <br />
      </React.Fragment>
    ));
  };

  const loginError = (error: number) => {
    switch (error) {
      case 401:
      case 404:
        return Constant.InvalidPassword;
      case 403:
        return Constant.MultipleAttemptFailure;
      case 500:
        return Constant.DefaultLoginError;
    }
  };
  const loginErrorMessage = (error: number) => {
    const errorMessage = loginError(error);
    return isDesktop ? <div className="error__message">{errorMessage}</div> : <div className="error__message">{formatContent(errorMessage)}</div>;
  };
  const generateUserGreeting = (userDetails: DecryptedUser, isDesktop: boolean) => {
    if (!userDetails || userDetails?.hp) {
      return "Welcome Back";
    }

    const lpn = userDetails.lpn?.toLowerCase();
    const isEligibleLeadProvider =
      lpn === LeadProvider.DigitalLeads.toLowerCase() || lpn === LeadProvider.Microsite.toLowerCase() || userDetails.id != null;

    if (isDesktop && isEligibleLeadProvider) {
      return "Sign in to continue your application";
    }
    if (!isDesktop && isEligibleLeadProvider) {
      return "Sign in to continue";
    }
    return "Welcome Back";
  };
  const userDetails = location.state as DecryptedUser;
  return (
    <><div className="login">
      <div className="login-form">
        <div className="login-form__container">
          <div className="login-form__content">
            <h1 className="login-form__header">{generateUserGreeting(userDetails, isDesktop)}</h1>
            {isLoginError && loginErrorMessage(error)}
            <div className="login-form__fields">
              <Form
                onSubmit={handleSubmit(async (data) => {
                  await onSubmit({
                    emailaddress: data.email as string,
                    password: data.password as string,
                  });
                })}
              >
                <FormInput
                  formHandle={register("email")}
                  inputProps={{
                    name: "email",
                    placeholder: "Enter email",
                    label: "Email Address",
                    errorMessage: errors.email?.message as string,
                  }} />
                <FormInput
                  formHandle={register("password")}
                  inputProps={{
                    name: "password",
                    type: passwordType(isPasswordVisible),
                    placeholder: "Enter password",
                    icon: passwordIcon(isPasswordVisible),
                    onIconClick: () => setIsPasswordVisible(!isPasswordVisible),
                    isIconPointer: true,
                    label: "Password",
                    errorMessage: errors.password?.message as string,
                  }} />
                <Button title="Continue" className="login-btn" />
              </Form>
            </div>
            <div className="login-form__footer">
              <a href="/forgotpassword" className="forgot-password">
                Forgot Password?
              </a>
              <span>
                <Consent text={featureFlag?.splitConsentEnabled ? Constant.PRIVACY_AND_TERMSOFUSE_CONSENT : Constant.LOGIN_PAGE_OLD_CONSENT} />
              </span>
            </div>
          </div>
          <div className="login-form__image">{isDesktop && <img src={homeGraphic} alt="login-graphic" />}</div>
        </div>
      </div>
    </div><AdaFooter /></>
  );
}
